import React, { useEffect, useState } from "react";
import { accountsApi } from "../api";
import { useI18n } from "../i18n";
import { showToast } from "../utils/toast";

export default function TransferModal({ open, onClose, transfer }) {
  const { t } = useI18n();
  const [fromId, setFromId] = useState(transfer?.fromAccountId || "");
  const [toId, setToId] = useState(transfer?.toAccountId || "");
  const [amount, setAmount] = useState(String(transfer?.amount || ""));
  const [note, setNote] = useState(transfer?.note || "");
  const [saving, setSaving] = useState(false);
  const [fromName, setFromName] = useState("");
  const [toName, setToName] = useState("");
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    setFromId(transfer?.fromAccountId || "");
    setToId(transfer?.toAccountId || "");
    setAmount(String(transfer?.amount || ""));
    setNote(transfer?.note || "");
    (async () => {
      try {
        // Load accounts for dropdowns and name mapping
        try {
          const accs = await accountsApi.list().catch(()=>[]);
          setAccounts(Array.isArray(accs) ? accs : []);
        } catch {}
        if (transfer?.transferId) {
          const t = await accountsApi.getTransfer(transfer.transferId);
          setFromId(t?.fromAccountId || "");
          setToId(t?.toAccountId || "");
          setFromName(t?.fromAccountName || t?.fromAccountId || "");
          setToName(t?.toAccountName || t?.toAccountId || "");
          setAmount(String(t?.amount || ""));
          setNote(t?.note || "");
        } else {
          const map = new Map((accounts||[]).map(a=>[a.accountId, a.name]));
          setFromName(map.get(transfer?.fromAccountId || '') || (transfer?.fromAccountId || ''));
          setToName(map.get(transfer?.toAccountId || '') || (transfer?.toAccountId || ''));
        }
      } catch {}
    })();
  }, [transfer]);

  if (!open) return null;

  const onSave = async () => {
    if (saving) return;
    const amt = Number(amount || 0);
    if (!fromId || !toId || fromId === toId || !amt || amt <= 0) {
      showToast({ message: 'Select different accounts and enter amount > 0', variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (transfer?.transferId) { await accountsApi.deleteTransfer(transfer.transferId); }
      await accountsApi.transfer({ fromAccountId: fromId, toAccountId: toId, amount: amt, note });
      // reconcile both accounts happens server-side
      if (onClose) onClose(true);
    } catch (e) {
      showToast({ message: String(e?.message || e), variant: 'error' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-green-900/10">
      <div className="w-full sm:w-[420px] bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-green-100">
        <div className="p-4 border-b border-green-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-800">{transfer?.transferId ? 'Edit Transfer' : 'New Transfer'}</div>
          <button className="text-sm text-gray-500" onClick={()=>onClose && onClose(false)}>✕</button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-2">
          {transfer?.transferId ? (
            <>
              <div>
                <div className="text-xs text-gray-600 mb-1">{t('acct.transfer.from') || 'From Account'}</div>
                <input className="border rounded px-2 py-1 text-sm bg-gray-100 w-full" value={`${fromName}`} readOnly />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">{t('acct.transfer.to') || 'To Account'}</div>
                <input className="border rounded px-2 py-1 text-sm bg-gray-100 w-full" value={`${toName}`} readOnly />
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="text-xs text-gray-600 mb-1">{t('acct.transfer.from') || 'From Account'}</div>
                <select className="border rounded px-2 py-1 text-sm w-full" value={fromId} onChange={(e)=>setFromId(e.target.value)}>
                  <option value="">{t('acct.transfer.from')}</option>
                  {(accounts||[]).map(a => (<option key={a.accountId} value={a.accountId}>{a.name}</option>))}
                </select>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">{t('acct.transfer.to') || 'To Account'}</div>
                <select className="border rounded px-2 py-1 text-sm w-full" value={toId} onChange={(e)=>setToId(e.target.value)}>
                  <option value="">{t('acct.transfer.to')}</option>
                  {(accounts||[]).map(a => (<option key={a.accountId} value={a.accountId}>{a.name}</option>))}
                </select>
              </div>
            </>
          )}
          <input className="border rounded px-2 py-1 text-sm" type="number" placeholder="Amount" value={amount} onChange={(e)=>setAmount(e.target.value)} />
          <input className="border rounded px-2 py-1 text-sm" placeholder="Note (optional)" value={note} onChange={(e)=>setNote(e.target.value)} />
        </div>
        <div className="p-4 flex gap-2">
          <button className={`flex-1 bg-green-600 text-white rounded-xl py-2 text-sm shadow ${saving?'opacity-60':''}`} disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="flex-1 bg-white border border-green-200 text-green-700 rounded-xl py-2 text-sm" onClick={()=>onClose && onClose(false)}>Cancel</button>
        </div>
      </div>
    </div>
  );
}


